import React, { useEffect, useReducer, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col } from 'reactstrap';
import type { SingleValue } from 'react-select';
import FormInput from '@/components/forms/FormInput';
import { AdminRegionInfoDto, CountrySubdivision, CreateRegionDto, UpdateRegionDto } from '@repo/common-types';
import { fetchCountrySubdivisionsRest } from '@/services/countries/countries-service';
// import { ActionMeta, MultiValue } from 'react-select';
import { fetchCountriesRest } from '@/services/countries/countries-service';
import FormAsyncSelect from '@/components/forms/FormAsyncSelect';
import { useQuery } from '@tanstack/react-query';

interface RegionFormModalProps {
  region?: AdminRegionInfoDto;
  onClose: () => void;
  onSubmit: (data: CreateRegionDto | UpdateRegionDto) => void;
  isLoading: boolean;
}

// Form data type
interface RegionFormData {
  name: string;
  countryIds: string[];
  subdivisionIds?: string[];
}

// Country selection option type for AsyncSelect
type CountrySelectOption = {
  value: string; // countryId
  label: string; // "Country Name (CODE)"
  country: {
    id: string;
    name: string;
    code: string;
  };
};

type SubdivisionSelectOption = {
  value: string; // subdivisionId
  label: string; // "Subdivision Name (CODE)"
  subdivision: {
    id: string;
    name: string;
    code: string;
    countryId: string;
  };
};
const pickColorForCountry = (countryId: string): string => {
  // Deterministic color by hashing id
  let hash = 0;
  for (let i = 0; i < countryId.length; i++) hash = (hash * 31 + countryId.charCodeAt(i)) >>> 0;
  return BADGE_CLASSES[hash % BADGE_CLASSES.length];
};

// Validation schema
const regionSchema = Yup.object().shape({
  name: Yup.string()
    .required('Region name is required')
    .min(2, 'Region name must be at least 2 characters')
    .max(100, 'Region name must not exceed 100 characters'),
  countryIds: Yup.array()
    .of(Yup.string().required())
    .min(1, 'At least one country is required')
    .required('Country is required'),
  subdivisionIds: Yup.array().of(Yup.string().required()),
});
interface ModalState {
  currentCountry: CountrySelectOption | null;
  currentSubdivision: SubdivisionSelectOption | null;
  subdivisionOptions: SubdivisionSelectOption[];
  loadingSubdivisions: boolean;
  countryMetaById: Record<string, CountrySelectOption>; // for labels
  subdivisionMetaById: Record<string, SubdivisionSelectOption>; // for labels
  subdivisionCountryById: Record<string, string>; // subdivisionId -> countryId
  countryColorById: Record<string, string>; // countryId -> badge class
}

type ModalAction =
  | { type: 'INIT_FROM_REGION'; payload?: { region?: AdminRegionInfoDto } }
  | { type: 'SET_CURRENT_COUNTRY'; payload: CountrySelectOption | null }
  | { type: 'SET_CURRENT_SUBDIVISION'; payload: SubdivisionSelectOption | null }
  | { type: 'SET_SUBDIVISION_OPTIONS'; payload: SubdivisionSelectOption[] }
  | { type: 'SET_LOADING_SUBDIVISIONS'; payload: boolean }
  | { type: 'UPSERT_COUNTRY_META_COLOR'; payload: CountrySelectOption }
  | { type: 'REMOVE_COUNTRY_COLOR'; payload: string }
  | { type: 'UPSERT_SUBDIVISION_META'; payload: SubdivisionSelectOption }
  | { type: 'MERGE_SUBDIVISION_META'; payload: Record<string, SubdivisionSelectOption> };

const reducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'INIT_FROM_REGION': {
      const r = action.payload?.region;
      const countryMetaById: Record<string, CountrySelectOption> = {};
      const countryColorById: Record<string, string> = {};
      (r?.countries || []).forEach((c) => {
        countryMetaById[c.id] = {
          value: c.id,
          label: `${c.name} (${c.code})`,
          country: { id: c.id, name: c.name, code: c.code },
        };
        countryColorById[c.id] = countryColorById[c.id] || pickColorForCountry(c.id);
      });
      const subdivisionMetaById: Record<string, SubdivisionSelectOption> = {};
      const subdivisionCountryById: Record<string, string> = {};
      r?.subdivisions?.forEach((s) => {
        subdivisionMetaById[s.id] = {
          value: s.id,
          label: `${s.name} (${s.code})`,
          subdivision: { id: s.id, name: s.name, code: s.code, countryId: s.countryId },
        };
        subdivisionCountryById[s.id] = s.countryId;
      });
      const subdivisionOptions = (r?.subdivisions || []).map((s: CountrySubdivision) => ({
        value: s.id,
        label: `${s.name} (${s.code})`,
        subdivision: { id: s.id, name: s.name, code: s.code, countryId: s.countryId },
      }));
      return {
        ...state,
        currentCountry: null,
        currentSubdivision: null,
        subdivisionOptions,
        loadingSubdivisions: false,
        countryMetaById,
        subdivisionMetaById,
        subdivisionCountryById,
        countryColorById,
      };
    }
    case 'SET_CURRENT_COUNTRY':
      return { ...state, currentCountry: action.payload };
    case 'SET_CURRENT_SUBDIVISION':
      return { ...state, currentSubdivision: action.payload };
    case 'SET_SUBDIVISION_OPTIONS':
      return { ...state, subdivisionOptions: action.payload };
    case 'SET_LOADING_SUBDIVISIONS':
      return { ...state, loadingSubdivisions: action.payload };
    case 'UPSERT_COUNTRY_META_COLOR': {
      const meta = action.payload;
      return {
        ...state,
        countryMetaById: { ...state.countryMetaById, [meta.value]: meta },
        countryColorById: {
          ...state.countryColorById,
          [meta.value]: state.countryColorById[meta.value] || pickColorForCountry(meta.value),
        },
      };
    }
    case 'REMOVE_COUNTRY_COLOR': {
      const { [action.payload]: _removed, ...rest } = state.countryColorById;
      return { ...state, countryColorById: rest };
    }
    case 'UPSERT_SUBDIVISION_META': {
      const opt = action.payload;
      return {
        ...state,
        subdivisionMetaById: { ...state.subdivisionMetaById, [opt.value]: opt },
        subdivisionCountryById: { ...state.subdivisionCountryById, [opt.value]: opt.subdivision.countryId },
      };
    }
    case 'MERGE_SUBDIVISION_META':
      return {
        ...state,
        subdivisionMetaById: { ...state.subdivisionMetaById, ...action.payload },
      };
    default:
      return state;
  }
};

// Fixed palette of bootstrap badge colors (dark-theme friendly)
const BADGE_CLASSES = ['bg-primary', 'bg-success', 'bg-info', 'bg-warning', 'bg-danger', 'bg-secondary'];

const RegionFormModal: React.FC<RegionFormModalProps> = ({ region, onClose, onSubmit, isLoading }) => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const isEditMode = !!region;

  const [state, dispatch] = useReducer(reducer, {
    currentCountry: null,
    currentSubdivision: null,
    subdivisionOptions: [],
    loadingSubdivisions: false,
    countryMetaById: {},
    subdivisionMetaById: {},
    subdivisionCountryById: {},
    countryColorById: {},
  });

  const {
    currentCountry,
    currentSubdivision,
    subdivisionOptions,
    loadingSubdivisions,
    countryMetaById,
    subdivisionMetaById,
    subdivisionCountryById,
    countryColorById,
  } = state;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    setValue,
    watch,
  } = useForm<RegionFormData>({
    resolver: yupResolver(regionSchema),
    defaultValues: {
      name: region?.name || '',
      countryIds: region?.countries?.map((c) => c.id),
      subdivisionIds: region?.subdivisions?.map((s) => s.id),
    },
    mode: 'onChange',
  });

  // Watch selected arrays for chips rendering
  const selectedCountryIds = watch('countryIds') || [];
  const selectedSubdivisionIds = watch('subdivisionIds') || [];

  // Load country options for AsyncSelect (following AgentDealFormModal pattern)
  const loadCountryOptions = async (inputValue: string): Promise<CountrySelectOption[]> => {
    if (inputValue.length < 2) return [];

    try {
      const response = await fetchCountriesRest({
        skip: 0,
        take: 25,
        filter: {
          search: inputValue,
        },
      });

      return response.countries.map((country) => ({
        value: country.id,
        label: `${country.name} (${country.code})`,
        country: {
          id: country.id,
          name: country.name,
          code: country.code,
        },
      }));
    } catch (error) {
      console.error('Error loading countries:', error);
      return [];
    }
  };

  // helpers to manage RHF arrays
  const addCountryId = (countryId: string, meta?: CountrySelectOption) => {
    const existing = new Set(selectedCountryIds);
    if (!existing.has(countryId)) {
      const next = [...selectedCountryIds, countryId];
      setValue('countryIds', next, { shouldValidate: true, shouldDirty: true });
    }
    // Ensure meta and color are cached for chips
    if (meta) {
      dispatch({ type: 'UPSERT_COUNTRY_META_COLOR', payload: meta });
    } else if (!state.countryColorById[countryId]) {
      dispatch({
        type: 'UPSERT_COUNTRY_META_COLOR',
        payload: {
          value: countryId,
          label: 'Country',
          country: { id: countryId, name: 'Country', code: '' },
        },
      });
    }
  };

  const removeCountryId = (countryId: string) => {
    const next = selectedCountryIds.filter((id: string) => id !== countryId);
    const nextSubs = (watch('subdivisionIds') || []).filter(
      (sid: string) => state.subdivisionCountryById[sid] !== countryId,
    );
    setValue('countryIds', next, { shouldValidate: true, shouldDirty: true });
    setValue('subdivisionIds', nextSubs, { shouldValidate: true, shouldDirty: true });
    dispatch({ type: 'REMOVE_COUNTRY_COLOR', payload: countryId });
  };

  const addSubdivisionId = (opt: SubdivisionSelectOption) => {
    const current = watch('subdivisionIds') || [];
    const exists = new Set(current);
    if (!exists.has(opt.value)) {
      const next = [...current, opt.value];
      setValue('subdivisionIds', next, { shouldValidate: true, shouldDirty: true });
      dispatch({ type: 'UPSERT_SUBDIVISION_META', payload: opt });
    }
  };

  const removeSubdivisionId = (id: string) => {
    const next = (watch('subdivisionIds') || []).filter((sid: string) => sid !== id);
    setValue('subdivisionIds', next, { shouldValidate: true, shouldDirty: true });
    // if this is the last subdivision for that country, also remove its country id
    const countryId = state.subdivisionCountryById[id];
    const remainingForCountry = next.filter((sid: string) => state.subdivisionCountryById[sid] === countryId).length;
    if (countryId && remainingForCountry === 0) removeCountryId(countryId);
  };

  // Handle country selection change (single)
  const handleCountryChange = async (newValue: SingleValue<CountrySelectOption>) => {
    // Cancel any pending subdivision fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    dispatch({ type: 'SET_CURRENT_SUBDIVISION', payload: null });

    if (!newValue) {
      dispatch({ type: 'SET_CURRENT_COUNTRY', payload: null });
      dispatch({ type: 'SET_SUBDIVISION_OPTIONS', payload: [] });
      dispatch({ type: 'SET_LOADING_SUBDIVISIONS', payload: false });
      return;
    }

    dispatch({ type: 'SET_LOADING_SUBDIVISIONS', payload: true });

    // Create new abort controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const data = await fetchCountrySubdivisionsRest(
        { skip: 0, take: 100, filter: { countryId: newValue.value } },
        { signal: controller.signal }, // Pass abort signal
      );

      const subs = data.subdivisions;

      if (!subs || subs.length === 0) {
        addCountryId(newValue.value, newValue);
        dispatch({ type: 'SET_CURRENT_COUNTRY', payload: null });
        dispatch({ type: 'SET_SUBDIVISION_OPTIONS', payload: [] });
      } else {
        dispatch({ type: 'SET_CURRENT_COUNTRY', payload: newValue });
        dispatch({ type: 'UPSERT_COUNTRY_META_COLOR', payload: newValue });
        const opts = subs.map((s) => ({
          value: s.id,
          label: `${s.name} (${s.code})`,
          subdivision: { id: s.id, name: s.name, code: s.code, countryId: s.countryId },
        }));
        dispatch({ type: 'SET_SUBDIVISION_OPTIONS', payload: opts });
        const metaMap = opts.reduce(
          (acc, o) => {
            acc[o.value] = o;
            return acc;
          },
          {} as Record<string, SubdivisionSelectOption>,
        );
        dispatch({ type: 'MERGE_SUBDIVISION_META', payload: metaMap });
      }
    } catch (error: any) {
      // Don't update state if request was aborted
      if (error.name === 'AbortError') {
        console.log('Country subdivision fetch aborted');
        return;
      }

      console.error('Error loading subdivisions for country', error);
      dispatch({ type: 'SET_CURRENT_COUNTRY', payload: null });
      dispatch({ type: 'SET_SUBDIVISION_OPTIONS', payload: [] });
    } finally {
      // Only update loading state if this controller wasn't aborted
      if (!controller.signal.aborted) {
        dispatch({ type: 'SET_LOADING_SUBDIVISIONS', payload: false });
      }

      // Clear the ref if this was the current controller
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  };

  const handleSubdivisionChange = (newValue: SingleValue<SubdivisionSelectOption>) => {
    // if country isnt yet added to country ids we should also add the country to the country ids

    if (newValue) {
      addSubdivisionId(newValue as SubdivisionSelectOption);
      addCountryId(newValue?.subdivision.countryId);
    }
    dispatch({ type: 'SET_CURRENT_SUBDIVISION', payload: null });
  };

  // no bulk load effect

  // Reset form when region changes
  useEffect(() => {
    if (region) {
      reset({
        name: region.name,
        countryIds: region.countries.map((c) => c.id),
        subdivisionIds: region.subdivisions.map((s) => s.id),
      });
      dispatch({ type: 'INIT_FROM_REGION', payload: { region } });
    } else {
      reset({
        name: '',
        countryIds: [],
        subdivisionIds: [],
      });
      dispatch({ type: 'INIT_FROM_REGION' });
    }
  }, [region, reset]);

  const onFormSubmit = (data: RegionFormData) => {
    const submitData: CreateRegionDto | UpdateRegionDto = {
      name: data.name,
      countryIds: data.countryIds || [],
      subdivisionIds: data.subdivisionIds || [],
    };

    onSubmit(submitData);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <Modal isOpen toggle={onClose} size="lg" className="modal-dialog-centered">
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <ModalHeader toggle={onClose} className="bg-dark px-4 py-3 text-white">
          {isEditMode ? 'Edit Region' : 'Create New Region'}
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col md={12}>
              <FormInput
                name="name"
                label="Region Name"
                placeholder="Enter region name..."
                control={control}
                required
              />
            </Col>

            <Col md={12}>
              <FormAsyncSelect
                name="countryIds"
                label="Country"
                loadOptions={loadCountryOptions}
                onChange={handleCountryChange}
                value={currentCountry}
                placeholder="Search a country..."
                errors={errors}
              >
                {selectedCountryIds.length > 0 && (
                  <div className="d-flex mt-2 flex-wrap gap-2">
                    {selectedCountryIds.map((cid: string) => (
                      <span
                        key={cid}
                        className={`badge d-flex align-items-center gap-2 ${countryColorById[cid] || 'bg-primary'}`}
                      >
                        {countryMetaById[cid]?.label || 'Unknown Country'}
                        <button
                          type="button"
                          className="btn-close btn-close-white btn-sm"
                          aria-label="Remove country"
                          onClick={() => removeCountryId(cid)}
                          style={{ filter: 'invert(1)' }}
                        />
                      </span>
                    ))}
                  </div>
                )}
              </FormAsyncSelect>
            </Col>

            {(selectedSubdivisionIds.length > 0 || currentCountry) && (
              <Col md={12}>
                <FormAsyncSelect
                  name="subdivisionIds"
                  defaultOptions={subdivisionOptions}
                  loadOptions={async (input) => {
                    const term = (input || '').toLowerCase();
                    return subdivisionOptions.filter((o) => o.label.toLowerCase().includes(term));
                  }}
                  onChange={handleSubdivisionChange}
                  value={currentSubdivision}
                  placeholder={
                    subdivisionOptions.length === 0
                      ? 'No subdivisions available for selected countries'
                      : 'Search a subdivision and press enter to add...'
                  }
                  isDisabled={isLoading || loadingSubdivisions || subdivisionOptions.length === 0}
                  label="States/Subdivisions"
                  errors={errors}
                >
                  {(selectedSubdivisionIds.length || 0) > 0 && (
                    <div className="d-flex mt-2 flex-wrap gap-2">
                      {selectedSubdivisionIds.map((sid: string) => {
                        const meta = subdivisionMetaById[sid] || subdivisionOptions.find((o) => o.value === sid);
                        const cId = meta?.subdivision.countryId || subdivisionCountryById[sid];
                        const badge = countryColorById[cId || ''] || 'bg-secondary';
                        return (
                          <span key={sid} className={`badge d-flex align-items-center gap-2 ${badge}`}>
                            {meta?.label || 'Subdivision'}
                            <button
                              type="button"
                              className="btn-close btn-close-white btn-sm"
                              aria-label="Remove subdivision"
                              onClick={() => removeSubdivisionId(sid)}
                              style={{ filter: 'invert(1)' }}
                            />
                          </span>
                        );
                      })}
                    </div>
                  )}
                </FormAsyncSelect>
              </Col>
            )}

            <Col md={12}>
              <div className="alert alert-warning">
                <strong>Business Rules:</strong>
                <ul className="mb-0 mt-2">
                  <li>Countries without subdivisions can only be in one region</li>
                  <li>
                    Countries with subdivisions can be in multiple regions when specific subdivisions are assigned
                  </li>
                  <li>Each subdivision can only be assigned to one region</li>
                  <li>Regions with active agent deals cannot be deleted</li>
                </ul>
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between border-top p-4">
          <div>
            {isEditMode && (
              <small className="text-muted">Last updated: {new Date(region!.updatedAt).toLocaleDateString()}</small>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button type="button" color="light" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={isLoading || !isValid || !isDirty}>
              {isLoading ? 'Saving...' : isEditMode ? 'Update Region' : 'Create Region'}
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default RegionFormModal;
